import clsx from "clsx";
import { startOfMonth, subMonths } from "date-fns";
import { useEffect, useState } from "react";
import { DateRange, DayPicker } from "react-day-picker";
import "react-day-picker/style.css";

import { Export } from "~/api/Api";
import Button from "~/components/ui/button/Button";
import Modal from "~/components/ui/modal/Modal";

import styles from "./ClientReports.module.scss";
import { describeRecipients } from "./delivery";
import {
  PERIOD_PRESETS,
  ReportPeriod,
  findRecentReportForPeriod,
  formatPeriod,
  isSamePeriod,
  periodDays,
  previousPeriod,
  validatePeriod,
} from "./period";

export type ClientReportRequest = { period: ReportPeriod; email: boolean };

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (request: ClientReportRequest) => void;
  isCreating: boolean;
  exports: Export[];
  /** Only team admins can email a report; the backend refuses it otherwise. */
  canEmail: boolean;
  recipientNames: string[];
  /** The site's recipient list, emailed along with the members. */
  listLabel?: string;
};

const defaultPeriod = () => PERIOD_PRESETS[0].value(new Date());

const CreateClientReportModal = ({
  isOpen,
  onClose,
  onSubmit,
  isCreating,
  exports,
  canEmail,
  recipientNames,
  listLabel,
}: Props) => {
  const [selection, setSelection] = useState<DateRange | undefined>(
    defaultPeriod,
  );
  const [month, setMonth] = useState(() => calendarMonth(defaultPeriod()));
  const [email, setEmail] = useState(false);

  // Each opening starts from the default period
  useEffect(() => {
    if (isOpen) {
      const period = defaultPeriod();

      setSelection(period);
      setMonth(calendarMonth(period));
      setEmail(false);
    }
  }, [isOpen]);

  const period: ReportPeriod | null =
    selection?.from && selection?.to
      ? { from: selection.from, to: selection.to }
      : null;
  const error = period ? validatePeriod(period) : null;
  const recentReport = period
    ? findRecentReportForPeriod(exports, period)
    : undefined;
  const canSubmit = Boolean(period && !error && !recentReport && !isCreating);
  // Emailing without recipients is refused (422).
  const hasRecipients = recipientNames.length > 0 || Boolean(listLabel);

  const applyPreset = (preset: ReportPeriod) => {
    setSelection(preset);
    setMonth(calendarMonth(preset));
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      modalLabel={<h3>Create Client Report</h3>}
      size="lg"
      width="fit-content"
      buttonLabel="Create Report"
      action={() =>
        period && onSubmit({ period, email: email && hasRecipients })
      }
      isDisabled={!canSubmit}
      isLoading={isCreating}
    >
      <div className={styles.periodPicker}>
        <div className={styles.presets}>
          {PERIOD_PRESETS.map((preset) => {
            const value = preset.value(new Date());
            const isActive = Boolean(period && isSamePeriod(period, value));

            return (
              <Button
                key={preset.label}
                outlined={!isActive}
                onClick={() => applyPreset(value)}
              >
                {preset.label}
              </Button>
            );
          })}
        </div>
        <div className={styles.calendar}>
          <DayPicker
            mode="range"
            selected={selection}
            onSelect={setSelection}
            month={month}
            onMonthChange={setMonth}
            numberOfMonths={2}
            disabled={{ after: new Date() }}
            endMonth={new Date()}
            weekStartsOn={1}
          />
        </div>
      </div>
      <div className={styles.periodSummary}>
        {period ? (
          <>
            <p>
              <strong>{formatPeriod(period)}</strong> ({periodDays(period)}{" "}
              {periodDays(period) === 1 ? "day" : "days"}), compared with{" "}
              {formatPeriod(previousPeriod(period))}
            </p>
            {error && <p className={styles.periodError}>{error}</p>}
            {!error && recentReport && (
              <p className={styles.periodError}>
                A report for this period was created less than an hour ago.
                Download it from the list, or pick another period.
              </p>
            )}
          </>
        ) : (
          <p>
            {selection?.from
              ? "Pick the last day of the period."
              : "Pick the first day of the period, or one of the presets."}
          </p>
        )}
      </div>
      {canEmail && (
        <label
          className={clsx(
            styles.emailOption,
            !hasRecipients && styles.emailOptionDisabled,
          )}
        >
          <input
            type="checkbox"
            checked={email && hasRecipients}
            disabled={!hasRecipients}
            onChange={(event) => setEmail(event.currentTarget.checked)}
          />
          {hasRecipients
            ? `Email it to ${describeRecipients(recipientNames, listLabel, 3)} when it's ready`
            : "Email it when it's ready: choose who gets reports under Report Delivery first"}
        </label>
      )}
    </Modal>
  );
};

// Show the period's end in the right-hand month of the two
const calendarMonth = (period: ReportPeriod) =>
  subMonths(startOfMonth(period.to), 1);

export default CreateClientReportModal;
